import { DownloadOutlined, EditOutlined, ReloadOutlined, UnlockOutlined } from '@ant-design/icons';
import { useModal } from '@ebay/nice-modal-react';
import { Alert, Button, Input, Modal, Spin, Tag, message } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import PPTpayModal from '../../Article/PPT/PPTpayModal';
import kf from '../../assets/kf.png';
import { get } from '../../axios/axios';
import { QUERY_TYPE_LABEL, queryOrder } from '../../pages/writing/api';
import { beginPendingPpt, getPendingPptEntitlement } from './pptEntitlement';
import styles from './index.module.scss';

/** 新版写作订单 MLW / MLWC / MLWU 与答辩PPT 付款单 MLPG：老接口（/ai/paper/query）里不会有，不用去查 */
const isNewFlowNo = (no) => /^ML(W|PG)/i.test(no);
const isPptOrderNo = (no) => /^MLPG/i.test(no);

const WRITING_STATUS = {
    GENERATING: { text: '生成中', color: 'processing' },
    PENDING: { text: '排队中', color: 'processing' },
    COMPLETE: { text: '已完成', color: 'green' },
    PARTIAL_FAILURE: { text: '部分失败', color: 'red' },
    STOPPED: { text: '已暂停', color: 'orange' },
};

export const OrderModal = ({ editBtnText = '编辑', handleOk, initialOrderNo = '' }) => {
    const [orderNo, setOrderNo] = useState(initialOrderNo);
    const [queriedOrderNo, setQueriedOrderNo] = useState('');
    const [loading, setLoading] = useState(false);
    const [orderInfo, setOrderInfo] = useState(null);
    const [error, setError] = useState('');
    const [singleOrderNo, setSingleOrderNo] = useState(null);
    // 新版写作订单（/ai/order/query）：只展示摘要，详情去订单结果页
    const [writingOrder, setWritingOrder] = useState(null);
    const [showServiceModal, setShowServiceModal] = useState(false);
    const usePPTProModal = useModal(PPTpayModal);
    const requestId = useRef(0);

    const queryOrderWithNo = useCallback(async (value) => {
        const credential = String(value || '').trim();
        if (!credential) {
            message.warning('请输入生成订单号、支付交易单号或已使用的兑换码');
            return;
        }
        const currentRequest = ++requestId.current;
        setLoading(true);
        setError('');
        setOrderInfo(null);
        setSingleOrderNo(null);
        setWritingOrder(null);
        setQueriedOrderNo(credential);
        // 老接口与新版订单查询同时发起；新版查到订单就用新版，否则沿用原来的老订单逻辑。
        // 新版单号在老接口里一定查不到，直接跳过，省一次请求也不在后端留一条「未查询到订单」的异常
        const legacyRequest = isNewFlowNo(credential)
            ? Promise.resolve({ success: false })
            : get('/ai/paper/query', { orderNo: credential }, { slient: true });
        legacyRequest.catch(() => {});
        try {
            // 新版查询报错（如限流、网络）不能当成「没有这个订单」：老接口也查不到时把真实原因显示出来
            let writingError = null;
            const writing = await queryOrder(credential).catch((err) => {
                writingError = err;
                return null;
            });
            if (currentRequest !== requestId.current) return;
            if (writing) {
                setWritingOrder(writing);
                return;
            }
            const res = await legacyRequest.catch((err) => ({ success: false, msg: err?.message }));
            if (res.success === false || !res.data) {
                if (isPptOrderNo(credential)) {
                    throw new Error('答辩PPT 的付款单号不能在这里查询，请从首页「我的生成记录」或付款后的页面进入答辩PPT');
                }
                const reason = writingError?.response?.data?.msg || writingError?.message;
                throw new Error(
                    reason && /[一-龥]/.test(reason)
                        ? reason
                        : res.msg || '未找到生成订单，请检查输入的凭证',
                );
            }
            if (currentRequest !== requestId.current) return;
            const result = { ...res.data, list: Array.isArray(res.data.list) ? res.data.list : [] };
            setOrderInfo(result);
            // 查询接口仅在单章订单尚需补款时返回 orderNo 和 orderPrice。
            setSingleOrderNo(result.orderNo && result.orderPrice != null ? result.orderNo : null);
            const hasPending = result.list.some((file) => !file.downloadUrl);
            if (
                hasPending &&
                result.createTime &&
                Date.now() - new Date(result.createTime).getTime() > 3600000
            )
                setShowServiceModal(true);
        } catch (err) {
            if (currentRequest === requestId.current)
                setError(err.response?.data?.msg || err.message || '查询失败，请稍后重试');
        } finally {
            if (currentRequest === requestId.current) setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (initialOrderNo) {
            setOrderNo(initialOrderNo);
            queryOrderWithNo(initialOrderNo);
        }
    }, [initialOrderNo, queryOrderWithNo]);
    useEffect(
        () => () => {
            ++requestId.current;
        },
        [],
    );

    const unlockAll = async () => {
        try {
            await usePPTProModal.show({
                title: '解锁全章',
                majorName: orderInfo.paperTitle,
                orderNo: singleOrderNo,
                price: orderInfo.orderPrice,
            });
            await queryOrderWithNo(queriedOrderNo);
            message.success('已成功解锁全章');
        } catch (_) {
            /* 用户关闭支付弹窗时保留当前查询结果。 */
        }
    };
    const isEditable = (url) =>
        url &&
        !url.includes('MLP') &&
        !url.includes('o=') &&
        url.toUpperCase().split('ML').length < 3;
    const edit = (downloadUrl, oldEdit) => {
        handleOk?.();
        const documentOrderNo = downloadUrl.split('/').pop();
        window.location.href = `${oldEdit === 'y' ? '/editor' : '/editor-v2'}?orderNo=${encodeURIComponent(documentOrderNo)}`;
    };
    const openWritingDetail = () => {
        handleOk?.();
        window.location.href = `/order/result?no=${encodeURIComponent(queriedOrderNo)}`;
    };
    // 新版订单正文：整篇解锁且全部生成完成后可进在线编辑器（后端按订单号镜像成编辑器文档）
    const openWritingEditor = () => {
        handleOk?.();
        window.location.href = `/editor-v2?orderNo=${encodeURIComponent(writingOrder.orderNo)}`;
    };
    const renderWritingOrder = () => {
        const docs = Array.isArray(writingOrder.documents) ? writingOrder.documents : [];
        const fileDocs = docs.filter((doc) => doc.kind !== 'PPT_CODE');
        const doneDocs = fileDocs.filter((doc) => doc.status === 'DONE').length;
        const unpaid = !writingOrder.purchaseMode || writingOrder.purchaseMode === 'UNPAID';
        const mainDoc = docs.find((doc) => doc.kind === 'MAIN');
        const canEditOnline =
            !!writingOrder.orderNo &&
            ['FULL', 'CHAPTER_TO_FULL'].includes(writingOrder.purchaseMode) &&
            writingOrder.generationStatus === 'COMPLETE' &&
            mainDoc?.status === 'DONE';
        const status = unpaid
            ? { text: '待支付', color: 'default' }
            : WRITING_STATUS[writingOrder.generationStatus] || { text: '处理中', color: 'processing' };
        const progress = Math.max(0, Math.min(100, Math.round(Number(writingOrder.progress) || 0)));
        return (
            <div className={styles.content}>
                <div className={styles.header}>
                    <span>查询凭证：{queriedOrderNo}</span>
                    <Button
                        type="text"
                        size="small"
                        icon={<ReloadOutlined />}
                        onClick={() => queryOrderWithNo(queriedOrderNo)}
                    >
                        刷新结果
                    </Button>
                </div>
                <div className={styles.writingCard}>
                    <div className={styles.writingHead}>
                        <strong>{writingOrder.paperTitle || '写作订单'}</strong>
                        <Tag color={status.color}>{writingOrder.statusText || status.text}</Tag>
                    </div>
                    <div className={styles.writingMeta}>
                        <span>{QUERY_TYPE_LABEL[writingOrder.queryType] || '写作'}</span>
                        {writingOrder.orderNo ? <span>订单号 {writingOrder.orderNo}</span> : null}
                        {fileDocs.length ? (
                            <span>
                                已完成 {doneDocs} / {fileDocs.length} 个文档
                            </span>
                        ) : null}
                    </div>
                    {!unpaid && writingOrder.generationStatus !== 'COMPLETE' ? (
                        <div className={styles.writingProgress} aria-label={`生成进度 ${progress}%`}>
                            <span style={{ width: `${progress}%` }} />
                        </div>
                    ) : null}
                    <div className={styles.writingActions}>
                        <span>预览、下载 Word、补差价解锁等请在订单详情中操作</span>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {canEditOnline ? (
                                <Button icon={<EditOutlined />} onClick={openWritingEditor}>
                                    在线编辑
                                </Button>
                            ) : null}
                            <Button type="primary" onClick={openWritingDetail}>
                                查看订单详情
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    const files = orderInfo?.list || [];
    const generatedFiles = files.filter((file) => !getPendingPptEntitlement(file.downloadUrl));
    const completed = generatedFiles.filter((file) => file.downloadUrl).length;
    const createPpt = (entitlement) => {
        try {
            beginPendingPpt(entitlement);
        } catch (err) {
            setError(err.message || '暂时无法进入PPT制作，请重试');
        }
    };

    return (
        <div className={styles.orderModal}>
            <p className={styles.intro}>
                查询生成订单后，可直接下载已完成的文件。套餐尚未使用时，请先到“我的套餐”开始创作。
            </p>
            <div className={styles.search}>
                <Input
                    value={orderNo}
                    aria-label="生成订单查询凭证"
                    placeholder="生成订单号 / 支付交易单号 / 已使用兑换码"
                    onChange={(event) => setOrderNo(event.target.value)}
                    onPressEnter={() => queryOrderWithNo(orderNo)}
                    allowClear
                />
                <Button loading={loading} type="primary" onClick={() => queryOrderWithNo(orderNo)}>
                    查询文件
                </Button>
            </div>
            {error && (
                <Alert
                    className={styles.notice}
                    showIcon
                    type={/生成中|处理中/.test(error) ? 'info' : 'warning'}
                    message={error}
                    action={
                        <Button size="small" onClick={() => queryOrderWithNo(queriedOrderNo)}>
                            重试
                        </Button>
                    }
                />
            )}
            <Spin spinning={loading}>
                {writingOrder ? (
                    renderWritingOrder()
                ) : orderInfo ? (
                    <div className={styles.content}>
                        <div className={styles.header}>
                            <span>查询凭证：{queriedOrderNo}</span>
                            <Button
                                type="text"
                                size="small"
                                icon={<ReloadOutlined />}
                                onClick={() => queryOrderWithNo(queriedOrderNo)}
                            >
                                刷新结果
                            </Button>
                        </div>
                        <div className={styles.heading}>
                            <h3>{orderInfo.paperTitle || '我的生成文件'}</h3>
                            <span>
                                {generatedFiles.length
                                    ? `已生成 ${completed} / ${generatedFiles.length} 个文件`
                                    : '暂无已生成文件'}
                            </span>
                        </div>
                        {(!files.length || completed < generatedFiles.length) && (
                            <Alert
                                className={styles.notice}
                                showIcon
                                type="info"
                                message="部分文件仍在生成中，已完成的文件可以先下载。"
                                description="稍后点击“刷新结果”查看最新进度。"
                            />
                        )}
                        {files.map((file, index) => {
                            const pptEntitlement = getPendingPptEntitlement(file.downloadUrl);
                            return (
                                <div className={styles.paperInfo} key={`${file.title}-${index}`}>
                                    <div className={styles.fileName}>
                                        <strong>
                                            {pptEntitlement
                                                ? '答辩PPT'
                                                : file.title || `文件 ${index + 1}`}
                                        </strong>
                                        <Tag
                                            color={
                                                pptEntitlement
                                                    ? 'gold'
                                                    : file.downloadUrl
                                                      ? 'green'
                                                      : 'processing'
                                            }
                                        >
                                            {pptEntitlement
                                                ? '待制作'
                                                : file.downloadUrl
                                                  ? '可下载'
                                                  : '生成中'}
                                        </Tag>
                                    </div>
                                    <div className={styles.fileActions}>
                                        {pptEntitlement ? (
                                            <Button
                                                type="primary"
                                                onClick={() => createPpt(pptEntitlement)}
                                            >
                                                制作答辩PPT
                                            </Button>
                                        ) : (
                                            <Button
                                                type={file.downloadUrl ? 'primary' : 'default'}
                                                icon={
                                                    file.downloadUrl ? <DownloadOutlined /> : null
                                                }
                                                disabled={!file.downloadUrl}
                                                href={file.downloadUrl || undefined}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                            >
                                                {file.downloadUrl ? '下载文件' : '生成中，请稍候'}
                                            </Button>
                                        )}
                                        {!pptEntitlement &&
                                            isEditable(file.downloadUrl) &&
                                            !singleOrderNo && (
                                                <Button
                                                    icon={<EditOutlined />}
                                                    onClick={() =>
                                                        edit(file.downloadUrl, file.oldEdit)
                                                    }
                                                >
                                                    {editBtnText}
                                                </Button>
                                            )}
                                    </div>
                                </div>
                            );
                        })}
                        {singleOrderNo && (
                            <div className={styles.footer}>
                                <p>当前为单章订单，可下载已生成章节，或继续解锁全文。</p>
                                <Button
                                    type="primary"
                                    onClick={unlockAll}
                                    icon={<UnlockOutlined />}
                                >
                                    解锁全文
                                </Button>
                            </div>
                        )}
                    </div>
                ) : !error && !loading ? (
                    <div className={styles.guide}>
                        <h3>如何找到并下载文件</h3>
                        <ol>
                            <li>输入支付账单中的交易单号，或生成时保存的订单号。</li>
                            <li>点击“查询文件”，查看每个文件的生成状态。</li>
                            <li>显示“可下载”后，点击对应的“下载文件”。</li>
                        </ol>
                        <p>使用套餐生成的文件，也可在“我的套餐”中直接查看。</p>
                    </div>
                ) : null}
            </Spin>
            <Modal
                title="文档生成耗时较长"
                open={showServiceModal}
                onCancel={() => setShowServiceModal(false)}
                footer={
                    <Button type="primary" onClick={() => setShowServiceModal(false)}>
                        知道了
                    </Button>
                }
                centered
                width={340}
            >
                <p>可先下载已完成的文件。如需帮助，请扫描二维码联系客服，并提供订单凭证。</p>
                <img
                    src={kf}
                    alt="客服二维码"
                    style={{ display: 'block', width: 160, height: 160, margin: '16px auto' }}
                />
            </Modal>
        </div>
    );
};
